import { DIAGNOSTIC_DEFINITION_KIND, getTRPCClient, memberKey, PROTOCOL_DEFINITION_KIND } from "@repo/api";
import type {
  CorrectDiagnosticObservationInput,
  CreateOrderSetsInput,
  MoveOrderInput,
  MoveStepInput,
  RecordDiagnosticObservationInput,
  RecordDiagnosticProcedureOutcomeInput,
  ReplaceOrderPlannedTrialsInput,
  ResetTimingInput,
  ResizeStepInput,
  RevokeOrderInput,
  SetDiagnosticProcedureNotesInput,
  SetOrderParameterInput,
} from "@repo/api/schemas";
import {
  type ApplyOrderSetChangesInput,
  applyOrderSetChangesInputSchema,
  createOrderSetsInputSchema,
  moveOrderInputSchema,
  moveStepInputSchema,
  type OrderSetDraftInput,
  orderSetDraftInputSchema,
  type RemoveOrderSetInput,
  removeOrderSetInputSchema,
  replaceOrderPlannedTrialsInputSchema,
  resetTimingInputSchema,
  resizeStepInputSchema,
  revokeOrderInputSchema,
  type SetOrderSetEnvelopeInput,
  setOrderParameterInputSchema,
  setOrderSetEnvelopeInputSchema,
} from "@repo/api/schemas";
import type { IntegerDeclaration, ProcedureTimeSource, QuantityDeclaration } from "@repo/domain";
import {
  asMemberId,
  asOrderId,
  asOrderSetId,
  asPatientId,
  isCurrentOrderSetStatus,
  memberIdSequence,
  OrderSet,
  type OrderSetProps,
  type OrderSetStatus,
  type OrderStatus,
  type PatientId,
  ProtocolDefinition,
  type StepPlacementProps,
} from "@repo/domain";
import type { Collection, InitialQueryBuilder, UtilsRecord } from "@tanstack/db";
import { and, eq, inArray, toArray, useLiveQuery } from "@tanstack/react-db";
import { defineWrite, type WriteOptions } from "../actions/define-write";
import { useCollection } from "../core/hooks";
import { getDataStoreClient } from "../core/provider";
import { ORDER_ID_URL, readOrderSet, writeOrderSetGraph } from "../fhir/order-set-storage";
import { readProtocolDefinition } from "../fhir/protocol-definition-storage";
import type {
  ActivityDefinitionRow,
  AppointmentRow,
  CarePlanRow,
  ObservationRow,
  PlanDefinitionRow,
  ProcedureRow,
  RequestGroupRow,
  ServiceRequestRow,
} from "../fhir/types";
import { createOrderSetCurationActions, curationExtensions } from "./_order-set-curation-actions";
import { createOrderSetDiagnosticActions } from "./_order-set-diagnostic-actions";
import { createOrderSetTreatmentProcedureActions } from "./_order-set-procedure-actions";
import { patientDomainId } from "./_patient-reference";
import {
  createProcedureTrialActions,
  type ProcedureTrialActions,
  type ProcedureTrialDependencies,
} from "./_procedure-trial-actions";
import { projectDiagnosticDefinitionRow } from "./diagnostic-definition-projection";
import { replayOrderSetChanges } from "./order-set-draft";
import { projectLoadedOrderSet } from "./order-set-projection";

export type { OrderSetStatus } from "@repo/domain";
export type OrderSetParameterValue =
  | { kind: "Quantity"; value: number; unit?: { code: string; display: string } }
  | { kind: "Integer"; value: number }
  | { kind: "Boolean"; value: boolean }
  | { kind: "Text"; value: string }
  | { kind: "Choice"; selected: readonly string[]; freeText?: string };

interface OrderSetParameterAnswerDto {
  source: string;
  parameterId: string;
  value: OrderSetParameterValue;
}

interface OrderSetPlannedTrialDto {
  id: string;
  values: readonly OrderSetParameterAnswerDto[];
}

interface OrderSetReadOrderDto {
  orderId: string;
  definition: { url: string; version: string };
  /** Display suffix distinguishing repeats of one definition. Never identity. */
  qualifier?: string;

  parameterValues: readonly OrderSetParameterAnswerDto[];
  observationLevels?: readonly import("@repo/domain").ObservationLevel[];
  /** Absent when none. An order with planned trials has no `parameterValues`. */
  plannedTrials?: readonly OrderSetPlannedTrialDto[];
  status: OrderStatus;
  sourceOccurrenceId?: string;
  revokedAt?: string;
  visitId?: string;
  procedureNote?: string;
  charted: boolean;
  notPerformed: boolean;
  observations: readonly OrderSetObservationDto[];
  chartingObservations: readonly OrderSetChartingObservationDto[];
  chartedProcedure?: OrderSetChartedProcedureDto;
  treatmentProcedure?: OrderSetTreatmentProcedureDto;
  diagnosticProcedure?: OrderSetDiagnosticProcedureDto;
}

type DiagnosticValueDefinition =
  | QuantityDeclaration
  | IntegerDeclaration
  | { kind: "Boolean" }
  | { kind: "Text"; maxLength?: number }
  | {
      kind: "Choice";
      options: readonly { id: string; label: string }[];
      cardinality: "Single" | "Multiple";
      allowFreeText?: boolean;
    };

interface OrderSetDiagnosticDeclarationDto {
  definitionUrl: string;
  version: number;
  name: string;
  description?: string;
  observations: readonly {
    id: string;
    label: string;
    repeatable: boolean;
    components: readonly { id: string; label: string; value: DiagnosticValueDefinition }[];
    noteLabel?: string;
  }[];
  procedureNoteLabel?: string;
}

type OrderSetDiagnosticRecordedValueDto =
  | { kind: "Quantity"; value: number; unit?: { code: string; display: string } }
  | { kind: "Integer"; value: number }
  | { kind: "Boolean"; value: boolean }
  | { kind: "Choice"; selected: string[]; freeText?: string }
  | { kind: "Text"; value: string };

interface OrderSetObservationDto {
  code: string;
  display?: string;
  value: string;
}

interface OrderSetDiagnosticProcedureDto {
  startedAt?: string;
  endedAt?: string;
  /** Medplum's last-writer stamp, standing in until diagnostics record a performer. */
  lastWrittenBy?: string;
  startSource?: ProcedureTimeSource;
  endSource?: ProcedureTimeSource;
  trials: readonly import("@repo/domain").ProcedureTrial[];
  id: string;
  uncaptured: readonly string[];
  /**
   * Where the procedure IS, which a resolved one also reports as `outcome`.
   *
   * Both, because they answer different questions: `outcome` is what the
   * clinician decided and is what gates editing, `status` is the lifecycle and
   * is what a badge shows. Projecting only the outcome left `InProgress`
   * indistinguishable from no procedure at all, so a diagnostic somebody was
   * part-way through badged "Not started" while the treatment beside it — which
   * has always carried a status — read "In progress".
   */
  status: "inProgress" | "performed" | "didNotPerform" | "unableToComplete";
  outcome?: "performed" | "didNotPerform" | "unableToComplete";
  observations: readonly {
    resourceId: string;
    observationId: string;
    trialId?: string;
    definitionId: string;
    observedAt: string;
    values: readonly { componentId: string; value: OrderSetDiagnosticRecordedValueDto }[];
    note?: string;
    /**
     * Where this number came from: the document it was read off, and whether
     * the value on screen is still that document's. `document` reads "From
     * <title>", `hand` reads "From <title>, edited since".
     *
     * `status` deliberately does NOT answer that second question, and reading
     * it as though it did was the bug. Overwriting a committed clinical value
     * is an amendment whoever did it, so an opt-in re-import of the same file
     * is `corrected` exactly as a typo fix is — and the card claimed a person
     * had edited a number the file had just written. A struck reading never
     * reaches this list, so those are still the only two statuses.
     */
    derivedFrom?: { documentId: string; writtenBy: "document" | "hand" };
    status: "final" | "corrected";
  }[];
  notes?: string;
}

interface OrderSetChartingObservationDto {
  observationId: string;
  code: { system?: string; code?: string; display?: string; text?: string };
  status: string;
  effectiveDateTime?: string;
  valueString?: string;
  valueQuantity?: { value?: number; unit?: string; system?: string; code?: string };
  valueCodeableConcept?: { system?: string; code?: string; display?: string; text?: string };
  valueInteger?: number;
  valueBoolean?: boolean;
  components?: readonly {
    code: { system?: string; code?: string; display?: string; text?: string };
    valueString?: string;
    valueQuantity?: { value?: number; unit?: string; system?: string; code?: string };
    valueCodeableConcept?: { system?: string; code?: string; display?: string; text?: string };
    valueInteger?: number;
    valueBoolean?: boolean;
  }[];
  notes?: readonly { text: string }[];
}

interface OrderSetChartedProcedureDto {
  procedureId: string;
  status: string;
  statusReason?: { code?: string; display?: string; text?: string };
  code: { code?: string; display?: string; text?: string };
  notes?: readonly { text: string }[];
}

export type OrderSetTreatmentProcedureStatus = "inProgress" | "performed" | "unableToComplete" | "didNotPerform";

type OrderSetTreatmentRecordedValueDto =
  | { kind: "Quantity"; value: number; unit?: { code: string; display: string } }
  | { kind: "Integer"; value: number }
  | { kind: "Boolean"; value: boolean }
  | { kind: "Choice"; selected: readonly string[]; freeText?: string }
  | { kind: "Text"; value: string };

interface OrderSetTreatmentProcedureDto {
  procedureId: string;
  status: OrderSetTreatmentProcedureStatus;
  performerIds: readonly string[];
  trials: readonly import("@repo/domain").ProcedureTrial[];
  performedPeriod?: { start: string; end?: string; startSource?: ProcedureTimeSource; endSource?: ProcedureTimeSource };
  note?: string;
  observations: readonly {
    trialId?: string;
    observationId: string;
    definitionId: string;
    observedAt: string;
    components: readonly { componentId: string; value: OrderSetTreatmentRecordedValueDto }[];
    note?: string;
    lifecycle: { status: "Final" } | { status: "Corrected" } | { status: "EnteredInError"; note?: string };
  }[];
  uncaptured: readonly string[];
}

/** Aggregate and execution read model. Catalog presentation belongs to consumers. */
export interface OrderSetReadDto {
  stepIdHigh?: number;
  authoredEnvelopeMinutes?: number;
  orderSetId: string;
  patientId: string;
  /** Absent for a set built procedure by procedure. */
  protocol?: { url: string; version: string };
  /** When the protocol was materialized onto this set. The instant to read it as of. */
  envelopeMinutes: number;
  durationMinutes: number;
  orderedAt: string;
  carePlanId?: string;
  curationRank?: number;
  status: OrderSetStatus;
  steps: readonly { stepId: string; placement: StepPlacementProps; orders: readonly OrderSetReadOrderDto[] }[];
  orders: readonly OrderSetReadOrderDto[];
  procedureCount: number;
  visitId?: string;
  currentOrder?: { orderId: string; definitionUrl: string };
}

export interface OrderSetFilters {
  ids?: readonly string[];
  patientId?: PatientId;
  carePlanId?: string;
  /**
   * One status, or several. Several is the common case for a plan in flight: a
   * set completes the moment its last procedure is charted, so filtering an
   * treatment plan to `active` alone drops each protocol out of the plan as it is
   * carried out.
   */
  status?: OrderSetStatus | readonly OrderSetStatus[];
  visitId?: string;
  protocol?: { url: string; version: string };
}

type OrderSetAnchors = Collection<RequestGroupRow, string, UtilsRecord>;
type OrderSetOrders = Collection<ServiceRequestRow, string, UtilsRecord>;
type OrderSetProcedures = Collection<ProcedureRow, string, UtilsRecord>;
type OrderSetObservations = Collection<ObservationRow, string, UtilsRecord>;
type VisitAppointments = Collection<AppointmentRow, string, UtilsRecord>;

type OrderSetCoreRow = Parameters<typeof projectLoadedOrderSet>[0];

export type OrderSetLookup =
  | { orderSetId: string | undefined; visitId?: never }
  | { visitId: string | undefined; orderSetId?: never };

type OrderSetQueryFilters = OrderSetFilters;

export function buildVisitOrderSetLocatorQuery(
  q: InitialQueryBuilder,
  appointments: VisitAppointments,
  visitId: string,
) {
  return q
    .from({ appointment: appointments })
    .where(({ appointment }) => eq(appointment.domainId, visitId))
    .select(({ appointment }) => ({ orderSetId: appointment.orderSetId }))
    .findOne();
}

export function buildOrderSetsQuery(
  q: InitialQueryBuilder,
  collections: {
    anchors: OrderSetAnchors;
    orders: OrderSetOrders;
    procedures: OrderSetProcedures;
    observations: OrderSetObservations;
  },
  filters: OrderSetQueryFilters,
) {
  const { ids, patientId, carePlanId, status, visitId, protocol } = filters;
  let query = q
    .from({ orderSet: collections.anchors })
    .where(({ orderSet }) => eq(orderSet.isRedesignedOrderSet, true));
  if (ids) query = query.where(({ orderSet }) => inArray(orderSet.domainId, [...ids]));
  if (patientId) query = query.where(({ orderSet }) => eq(orderSet.patientId, patientId));
  if (carePlanId) query = query.where(({ orderSet }) => eq(orderSet.carePlanId, carePlanId));
  if (Array.isArray(status)) query = query.where(({ orderSet }) => inArray(orderSet.status, [...status]));
  else if (status) query = query.where(({ orderSet }) => eq(orderSet.status, status as OrderSetStatus));
  if (visitId) query = query.where(({ orderSet }) => eq(orderSet.visitId, visitId));
  if (protocol) {
    query = query.where(({ orderSet }) =>
      and(eq(orderSet.protocolUrl, protocol.url), eq(orderSet.protocolVersion, protocol.version)),
    );
  }
  const chartedProcedures = q.from({ procedure: collections.procedures }).select(({ procedure }) => ({
    ...procedure,
    observations: toArray(
      q
        .from({ observation: collections.observations })
        .where(({ observation }) => eq(observation.procedureJoinKey, procedure.domainId))
        .select(({ observation }) => ({ ...observation })),
    ),
  }));
  const intermediate = query.select(({ orderSet }) => ({
    anchor: { ...orderSet },
    orders: toArray(
      q
        .from({ order: collections.orders })
        .where(({ order }) => eq(order.orderSetJoinKey, orderSet.domainId))
        .select(({ order }) => ({
          ...order,
          procedures: toArray(
            q
              .from({ procedure: chartedProcedures })
              // BOTH halves, always. `orderId` is a member id scoped to its
              // OrderSet, so on its own it matches the identically-numbered
              // order of every other set realized from the same protocol —
              // the reschedule/replacement flow makes exactly that pair. The
              // child's keys are sentinel-guarded, so a record naming no set
              // or no order matches nothing rather than everything.
              .where(({ procedure }) =>
                and(eq(procedure.orderSetJoinKey, orderSet.domainId), eq(procedure.orderJoinKey, order.orderId)),
              )
              .select(({ procedure }) => ({ ...procedure })),
          ),
          observations: toArray(
            q
              .from({ observation: collections.observations })
              // Parent-scoped for the same reason the procedure join above is.
              .where(({ observation }) =>
                and(eq(observation.orderSetJoinKey, orderSet.domainId), eq(observation.orderJoinKey, order.orderId)),
              )
              .select(({ observation }) => ({ ...observation })),
          ),
        })),
    ),
  }));
  return q.from({ row: intermediate }).select(({ row }) => ({ ...row }));
}

function projectOrderSetCore(row: OrderSetCoreRow): OrderSetReadDto | undefined {
  if (!Array.isArray(row.orders)) return undefined;
  return projectLoadedOrderSet({
    anchor: row.anchor,
    orders: row.orders,
  });
}

export function useOrderSets(filters: OrderSetFilters = {}): OrderSetReadDto[] {
  const anchors = useCollection("requestGroup");
  const orders = useCollection("serviceRequest");
  const procedures = useCollection("procedure");
  const observations = useCollection("observation");
  const result = useLiveQuery({
    queryKey: [
      anchors.id,
      orders.id,
      procedures.id,
      observations.id,
      "order-sets",
      filters.ids ?? null,
      filters.patientId ?? null,
      filters.carePlanId ?? null,
      Array.isArray(filters.status) ? filters.status.join(",") : (filters.status ?? null),
      filters.visitId ?? null,
      filters.protocol?.url ?? null,
      filters.protocol?.version ?? null,
    ],
    query: (q) => {
      if (filters.ids?.length === 0) return undefined;
      return buildOrderSetsQuery(q, { anchors, orders, procedures, observations }, filters);
    },
  });

  const projected = ((result.data as OrderSetCoreRow[] | undefined) ?? []).flatMap((row) => {
    const orderSet = projectOrderSetCore(row);
    return orderSet ? [orderSet] : [];
  });
  return inReadOrder(projected, filters.ids);
}

/**
 * A caller-provided id list wins; otherwise curation rank. Both sorts live
 * outside the query because an orderBy on the subquery does not survive the
 * outer select, and one on the outer select throws on optimistic insert.
 */
export function inReadOrder(sets: readonly OrderSetReadDto[], ids?: readonly string[]): OrderSetReadDto[] {
  if (ids) {
    const position = new Map(ids.map((id, index) => [id, index]));
    return [...sets].sort(
      (left, right) => (position.get(left.orderSetId) ?? 0) - (position.get(right.orderSetId) ?? 0),
    );
  }
  return [...sets].sort(
    (left, right) => (left.curationRank ?? Number.MAX_SAFE_INTEGER) - (right.curationRank ?? Number.MAX_SAFE_INTEGER),
  );
}

export function useOrderSet(filter: OrderSetLookup | undefined): OrderSetReadDto | undefined {
  const anchors = useCollection("requestGroup");
  const appointments = useCollection("appointment");
  const orders = useCollection("serviceRequest");
  const procedures = useCollection("procedure");
  const observations = useCollection("observation");
  const visitId = filter && "visitId" in filter ? filter.visitId : undefined;
  const orderSetId = filter && "orderSetId" in filter ? filter.orderSetId : undefined;
  const locatorResult = useLiveQuery({
    queryKey: [appointments.id, "order-set-locator", visitId ?? null],
    query: (q) => (visitId ? buildVisitOrderSetLocatorQuery(q, appointments, visitId) : undefined),
  });
  const visitOrderSetId = (locatorResult.data as { orderSetId?: string } | undefined)?.orderSetId;
  const lookupId = orderSetId ?? visitOrderSetId;
  const result = useLiveQuery({
    queryKey: [anchors.id, orders.id, procedures.id, observations.id, "order-set", lookupId ?? null],
    query: (q) => {
      if (!lookupId) return undefined;
      return buildOrderSetsQuery(q, { anchors, orders, procedures, observations }, { ids: [lookupId] }).findOne();
    },
  });
  const row = result.data as OrderSetCoreRow | undefined;
  return row ? projectOrderSetCore(row) : undefined;
}

const requestGroups = () => getDataStoreClient().getCollection("requestGroup");
const serviceRequests = () => getDataStoreClient().getCollection("serviceRequest");
const carePlanRows = () => getDataStoreClient().getCollection<CarePlanRow, string>("carePlan");
const protocolRows = () => getDataStoreClient().getCollection<PlanDefinitionRow, string>("planDefinition");
const definitionRows = () => getDataStoreClient().getCollection<ActivityDefinitionRow, string>("activityDefinition");
const procedureRows = () => getDataStoreClient().getCollection("procedure");
const observationRows = () => getDataStoreClient().getCollection("observation");

function orderSetAction<T>(
  schema: { parse(input: T): T },
  optimistic: (input: T) => void,
  mutate: (input: T, options: WriteOptions) => Promise<unknown>,
) {
  return defineWrite<T>({
    optimistic: (params) => optimistic(schema.parse(params)),
    persist: (params, options) => mutate(params, options),
  });
}

function schedulingFacts(orderSetId: string | undefined) {
  const appointments = getDataStoreClient().getCollection<AppointmentRow, string>("appointment");
  return {
    booked: appointments.toArray.some(
      (appointment) => appointment.orderSetId === orderSetId && appointment.status !== "cancelled",
    ),
  };
}

function graphFor(orderSetId: string): {
  props: OrderSetProps;
  prior: { anchor: RequestGroupRow; children: ServiceRequestRow[] };
} {
  const anchor = requestGroups().get(orderSetId);
  if (!anchor) throw new Error(`OrderSet '${orderSetId}' is not loaded`);
  const children = serviceRequests().toArray.filter((row) => row.orderSetId === orderSetId);
  const prior = { anchor, children };
  return { props: readOrderSet(anchor, children), prior };
}

function writeGraph(props: OrderSetProps, prior?: { anchor: RequestGroupRow; children: ServiceRequestRow[] }): void {
  props = OrderSet.restore(props).snapshot();
  const graph = writeOrderSetGraph(props, prior);
  const canonical = props.protocol;
  const row: RequestGroupRow = {
    ...(graph.anchor as RequestGroupRow),
    domainId: props.id,
    isRedesignedOrderSet: true,
    patientId: props.patientId,
    protocolUrl: canonical?.url,
    protocolVersion: canonical?.version,
    carePlanId: props.carePlanId,
    curationRank: props.curationRank,
    memberCount: graph.children.length,
    visitId: props.steps.flatMap((step) => step.orders).find((order) => order.visitId)?.visitId,
  };
  if (requestGroups().has(props.id)) requestGroups().update(props.id, (draft) => Object.assign(draft, row));
  else requestGroups().insert(row);
  for (const resource of graph.children as ServiceRequestRow[]) {
    const orderId = resource.extension?.find((extension) => extension.url === ORDER_ID_URL)?.valueString;
    if (!orderId) continue;
    const member: ServiceRequestRow = {
      ...resource,
      orderSetId: props.id,
      orderSetJoinKey: props.id,
      orderId,
      definitionCanonical: resource.instantiatesCanonical?.[0],
    };
    const key = memberKey(props.id, orderId);
    if (serviceRequests().has(key)) serviceRequests().update(key, (draft) => Object.assign(draft, member));
    else serviceRequests().insert(member);
  }
  // As on the server, an order the aggregate no longer holds is deleted: only a discarded draft drops one.
  const held = new Set(props.steps.flatMap((step) => step.orders.map((order) => order.orderId as string)));
  for (const child of prior?.children ?? []) {
    if (child.orderId && !held.has(child.orderId)) serviceRequests().delete(memberKey(props.id, child.orderId));
  }
}

function selectedProtocol(url: string, version: number): ProtocolDefinition {
  const row = protocolRows().toArray.find(
    (candidate) =>
      candidate.catalogModelKind === PROTOCOL_DEFINITION_KIND &&
      candidate.url === url &&
      candidate.catalogVersion === version,
  );
  if (!row) throw new Error(`Protocol '${url}|${version}' is not loaded`);
  return new ProtocolDefinition(readProtocolDefinition(row));
}

function definitionPin(url: string): { url: string; version: string; availability: "Active"; isCurrent: true } {
  const row = definitionRows()
    .toArray.filter((candidate) => candidate.url === url && candidate.status === "active")
    .sort((left, right) => right.catalogVersion - left.catalogVersion)[0];
  if (!row?.version) throw new Error(`Procedure definition '${url}' is not loaded`);
  return { url, version: row.version, availability: "Active", isCurrent: true };
}

export const createOrderSets = defineWrite<CreateOrderSetsInput>({
  optimistic: (params) => {
    const input = createOrderSetsInputSchema.parse(params);
    const plan = carePlanRows().toArray.find((row) => row.domainId === input.carePlanId);
    if (!plan) throw new Error(`Care plan '${input.carePlanId}' is not loaded`);
    const planRows = requestGroups().toArray.filter((row) => row.carePlanId === input.carePlanId);
    const nextRank = planRows.reduce((highest, row) => Math.max(highest, row.curationRank ?? -1), -1) + 1;
    const active = planRows
      .filter((row) => isCurrentOrderSetStatus(row.status))
      .sort((left, right) => (left.curationRank ?? 0) - (right.curationRank ?? 0));
    const position = input.position ?? active.length;
    const ranks = [
      ...active.map((row) => row.curationRank ?? 0),
      ...input.orderSets.map((_, offset) => nextRank + offset),
    ].sort((left, right) => left - right);
    for (const [index, row] of active.entries()) {
      const curationRank = ranks[index < position ? index : index + input.orderSets.length] ?? index;
      if (row.curationRank === curationRank || !row.domainId) continue;
      requestGroups().update(row.domainId, (draft) => {
        draft.curationRank = curationRank;
        draft.extension = curationExtensions(row, curationRank);
      });
    }
    for (const [offset, selected] of input.orderSets.entries()) {
      const optimisticId = asOrderSetId(selected.id);
      const curationRank = ranks[position + offset] ?? nextRank + offset;
      const patientId = asPatientId(patientDomainId(plan.subject));
      if (!("protocolUrl" in selected)) {
        writeGraph(
          OrderSet.createEmpty({
            id: optimisticId,
            patientId,
            orderedAt: new Date().toISOString(),
            carePlanId: input.carePlanId,
            curationRank,
          }).snapshot(),
        );
        continue;
      }
      const protocol = selectedProtocol(selected.protocolUrl, selected.expectedVersion);
      const stepIds = memberIdSequence("step");
      const props: OrderSetProps = {
        id: optimisticId,
        patientId,
        protocol: { url: protocol.url, version: String(protocol.version) },
        // Provisional: the server stamps its own from the session clock when the
        // set lands. Present because the aggregate requires it, not because
        // the client is the authority on when materialization happened.
        envelopeMinutes: protocol.durationMinutes,
        orderedAt: new Date().toISOString(),
        carePlanId: input.carePlanId,
        curationRank,
        status: "active",
        stepIdHigh: protocol.facts.steps.length,
        steps: protocol.facts.steps.map((step) => ({
          id: stepIds.next(),
          // Copy authored hold time; occurrence maxima are not the step extent.
          placement: { ...step.placement },
          orders: step.occurrences.map((occurrence) => ({
            orderId: asOrderId(occurrence.id),
            definition: definitionPin(occurrence.definition),
            sourceOccurrenceId: asMemberId(occurrence.id),

            parameterValues: occurrence.parameterValues.map((answer) => ({
              source: answer.source === "self" ? "self" : asMemberId(answer.source),
              parameterId: asMemberId(answer.parameterId),
              value:
                answer.value.kind === "Choice"
                  ? { ...answer.value, selected: answer.value.selected.map(asMemberId) }
                  : answer.value,
            })),
            ...(occurrence.observationLevels
              ? { observationLevels: structuredClone(occurrence.observationLevels) }
              : {}),
            ...(occurrence.plannedTrials ? { plannedTrials: structuredClone([...occurrence.plannedTrials]) } : {}),
            status: "draft",
          })),
        })),
      };
      writeGraph(props);
    }
  },
  persist: (input, options) => getTRPCClient().orderSet.create.mutate(input, options),
});

const curationActions = createOrderSetCurationActions({
  collections: () => ({
    requestGroups: requestGroups(),
  }),
  persist: {
    reorder: (input, options) => getTRPCClient().orderSet.reorder.mutate(input, options),
  },
});

export const removeOrderSet = orderSetAction(
  removeOrderSetInputSchema,
  (input: RemoveOrderSetInput) => {
    const { props, prior } = graphFor(input.orderSetId);
    const set = OrderSet.restore(props);
    set.removeFromCuration(schedulingFacts(input.orderSetId));
    writeGraph(set.snapshot(), prior);
  },
  (input: RemoveOrderSetInput, options: WriteOptions) => getTRPCClient().orderSet.remove.mutate(input, options),
);
export const reorderOrderSet = curationActions.reorder;

export const setOrderSetEnvelope = orderSetAction(
  setOrderSetEnvelopeInputSchema,
  (input: SetOrderSetEnvelopeInput) => {
    const { props, prior } = graphFor(input.orderSetId);
    const set = OrderSet.restore(props);
    set.setEnvelopeDuration(input.envelopeMinutes, schedulingFacts(prior.anchor.domainId));
    writeGraph(set.snapshot(), prior);
  },
  (input: SetOrderSetEnvelopeInput, options: WriteOptions) =>
    getTRPCClient().orderSet.setEnvelope.mutate(input, options),
);
export const setOrderParameter = orderSetAction(
  setOrderParameterInputSchema,
  (input: SetOrderParameterInput) => {
    const { props, prior } = graphFor(input.orderSetId);
    const order = props.steps.flatMap((step) => step.orders).find((candidate) => candidate.orderId === input.orderId);
    if (order) {
      const source = input.source === "self" ? ("self" as const) : asMemberId(input.source);
      const parameterId = asMemberId(input.parameterId);
      order.parameterValues = order.parameterValues.filter(
        (answer) => answer.source !== source || answer.parameterId !== parameterId,
      );
      if (input.value !== null) {
        order.parameterValues.push({
          source,
          parameterId,
          value:
            input.value.kind === "Choice"
              ? { ...input.value, selected: input.value.selected.map(asMemberId) }
              : input.value,
        });
      }
    }
    writeGraph(props, prior);
  },
  (input: SetOrderParameterInput, options: WriteOptions) =>
    getTRPCClient().orderSet.setParameter.mutate(input, options),
);
export const replaceOrderPlannedTrials = orderSetAction(
  replaceOrderPlannedTrialsInputSchema,
  (input: ReplaceOrderPlannedTrialsInput) => {
    const { props, prior } = graphFor(input.orderSetId);
    const order = props.steps.flatMap((step) => step.orders).find((candidate) => candidate.orderId === input.orderId);
    if (order) {
      const plannedTrials = input.plannedTrials.map((trial) => ({
        id: trial.id,
        values: trial.values.map((answer) => ({
          source: answer.source === "self" ? ("self" as const) : asMemberId(answer.source),
          parameterId: asMemberId(answer.parameterId),
          value:
            answer.value.kind === "Choice"
              ? { ...answer.value, selected: answer.value.selected.map(asMemberId) }
              : answer.value,
        })),
      }));
      if (plannedTrials.length) order.plannedTrials = plannedTrials;
      else delete order.plannedTrials;
    }
    writeGraph(props, prior);
  },
  (input: ReplaceOrderPlannedTrialsInput, options: WriteOptions) =>
    getTRPCClient().orderSet.replacePlannedTrials.mutate(input, options),
);
export const revokeOrder = orderSetAction(
  revokeOrderInputSchema,
  (input: RevokeOrderInput) => {
    const { props, prior } = graphFor(input.orderSetId);
    const order = props.steps.flatMap((step) => step.orders).find((candidate) => candidate.orderId === input.orderId);
    if (order) {
      order.status = "revoked";
      order.revokedAt = new Date().toISOString();
    }
    writeGraph(props, prior);
  },
  (input: RevokeOrderInput, options: WriteOptions) => getTRPCClient().orderSet.revokeOrder.mutate(input, options),
);
export const moveOrder = orderSetAction(
  moveOrderInputSchema,
  (input: MoveOrderInput) => {
    const { props, prior } = graphFor(input.orderSetId);
    const set = OrderSet.restore(props);
    set.moveOrder(input.orderId, input.toStep, schedulingFacts(prior.anchor.domainId));
    writeGraph(set.snapshot(), prior);
  },
  (input: MoveOrderInput, options: WriteOptions) => getTRPCClient().orderSet.moveOrder.mutate(input, options),
);
function diagnosticDeclarationFor(order: ServiceRequestRow): OrderSetDiagnosticDeclarationDto {
  const canonical = order.instantiatesCanonical?.[0];
  const definition = definitionRows().toArray.find(
    (candidate) =>
      candidate.catalogModelKind === DIAGNOSTIC_DEFINITION_KIND && candidate.definitionCanonical === canonical,
  );
  if (!definition) throw new Error(`DiagnosticDefinition '${canonical ?? "unknown"}' is not loaded`);
  // The catalog projection already assembles the standard and authored
  // observations into the order the clinician charts them in, which is exactly
  // the declaration this card needs. Two spellings of that list would drift.
  const catalog = projectDiagnosticDefinitionRow(definition);
  return {
    definitionUrl: catalog.definitionUrl,
    version: catalog.version,
    name: catalog.name,
    ...(catalog.description === undefined ? {} : { description: catalog.description }),
    observations: catalog.chartedObservations,
    ...(catalog.procedureNoteLabel === undefined ? {} : { procedureNoteLabel: catalog.procedureNoteLabel }),
  };
}

const diagnosticActions = createOrderSetDiagnosticActions({
  adjustTimes: (input, options) =>
    getTRPCClient().charting.diagnosticProcedure.adjustProcedureTimes.mutate(input, options),
  importDocument: (input, options) =>
    getTRPCClient().charting.diagnosticProcedure.importDocument.mutate(input, options),
  requestGroups,
  serviceRequests,
  procedures: procedureRows,
  observations: observationRows,
  declarationFor: diagnosticDeclarationFor,
  recordOutcome: (input: RecordDiagnosticProcedureOutcomeInput, options: WriteOptions) =>
    getTRPCClient().charting.diagnosticProcedure.recordOutcome.mutate(input, options),
  reopen: (input, options) => getTRPCClient().charting.diagnosticProcedure.reopen.mutate(input, options),
  retractDidNotPerform: (input, options) =>
    getTRPCClient().charting.diagnosticProcedure.retractDidNotPerform.mutate(input, options),
  markObservationUnableToCapture: (input, options) =>
    getTRPCClient().charting.diagnosticProcedure.markObservationUnableToCapture.mutate(input, options),
  clearObservationUnableToCapture: (input, options) =>
    getTRPCClient().charting.diagnosticProcedure.clearObservationUnableToCapture.mutate(input, options),
  recordObservations: (input, options) =>
    getTRPCClient().charting.diagnosticProcedure.recordObservations.mutate(input, options),
  recordObservation: (input: RecordDiagnosticObservationInput, options: WriteOptions) =>
    getTRPCClient().charting.diagnosticProcedure.recordObservation.mutate(input, options),
  retractObservation: (input, options) =>
    getTRPCClient().charting.diagnosticProcedure.retractObservation.mutate(input, options),
  correctObservation: (input: CorrectDiagnosticObservationInput, options: WriteOptions) =>
    getTRPCClient().charting.diagnosticProcedure.correctObservation.mutate(input, options),
  setNotes: (input: SetDiagnosticProcedureNotesInput, options: WriteOptions) =>
    getTRPCClient().charting.diagnosticProcedure.setNotes.mutate(input, options),
});

export const adjustDiagnosticProcedureTimes = diagnosticActions.adjustTimes;
export const recordDiagnosticProcedureOutcome = diagnosticActions.recordOutcome;
export const reopenDiagnosticProcedure = diagnosticActions.reopen;
export const retractDiagnosticProcedureDidNotPerform = diagnosticActions.retractDidNotPerform;
export const recordDiagnosticObservations = diagnosticActions.recordObservations;
export const recordDiagnosticObservation = diagnosticActions.recordObservation;
export const correctDiagnosticObservation = diagnosticActions.correctObservation;
export const retractDiagnosticObservation = diagnosticActions.retractObservation;
export const setDiagnosticProcedureNotes = diagnosticActions.setNotes;

const treatmentProcedureActions = createOrderSetTreatmentProcedureActions({
  adjustTimes: (input, options) =>
    getTRPCClient().charting.treatmentProcedure.adjustProcedureTimes.mutate(input, options),
  procedures: procedureRows,
  observations: observationRows,
  serviceRequests,
  requestGroups,
  complete: (input, options) => getTRPCClient().charting.treatmentProcedure.complete.mutate(input, options),
  markUnableToComplete: (input, options) =>
    getTRPCClient().charting.treatmentProcedure.markUnableToComplete.mutate(input, options),
  markDidNotPerform: (input, options) =>
    getTRPCClient().charting.treatmentProcedure.markDidNotPerform.mutate(input, options),
  reopen: (input, options) => getTRPCClient().charting.treatmentProcedure.reopen.mutate(input, options),
  retractDidNotPerform: (input, options) =>
    getTRPCClient().charting.treatmentProcedure.retractDidNotPerform.mutate(input, options),
  recordObservation: (input, options) =>
    getTRPCClient().charting.treatmentProcedure.recordObservation.mutate(
      {
        ...input,
        components: input.components.map((component) => ({
          ...component,
          value:
            component.value.kind === "Choice"
              ? { ...component.value, selected: [...component.value.selected] }
              : component.value,
        })),
      },
      options,
    ),
  correctObservation: (input, options) =>
    getTRPCClient().charting.treatmentProcedure.correctObservation.mutate(
      {
        ...input,
        components: input.components.map((component) => ({
          ...component,
          value:
            component.value.kind === "Choice"
              ? { ...component.value, selected: [...component.value.selected] }
              : component.value,
        })),
      },
      options,
    ),
  retractObservation: (input, options) =>
    getTRPCClient().charting.treatmentProcedure.retractObservation.mutate(input, options),
  clearObservationUnableToCapture: (input, options) =>
    getTRPCClient().charting.treatmentProcedure.clearObservationUnableToCapture.mutate(input, options),
  markObservationUnableToCapture: (input, options) =>
    getTRPCClient().charting.treatmentProcedure.markObservationUnableToCapture.mutate(input, options),
  setNote: (input, options) => getTRPCClient().charting.treatmentProcedure.setNote.mutate(input, options),
});

export const adjustTreatmentProcedureTimes = treatmentProcedureActions.adjustTimes;
export const completeTreatmentProcedure = treatmentProcedureActions.complete;
export const markTreatmentProcedureUnableToComplete = treatmentProcedureActions.markUnableToComplete;
export const markTreatmentProcedureDidNotPerform = treatmentProcedureActions.markDidNotPerform;
export const reopenTreatmentProcedure = treatmentProcedureActions.reopen;
export const retractTreatmentProcedureDidNotPerform = treatmentProcedureActions.retractDidNotPerform;
export const recordTreatmentProcedureObservation = treatmentProcedureActions.recordObservation;
export const correctTreatmentProcedureObservation = treatmentProcedureActions.correctObservation;
export const retractTreatmentProcedureObservation = treatmentProcedureActions.retractObservation;
export const markTreatmentProcedureObservationUnableToCapture =
  treatmentProcedureActions.markObservationUnableToCapture;
export const setTreatmentProcedureNote = treatmentProcedureActions.setNote;

export const clearTreatmentProcedureObservationUnableToCapture =
  treatmentProcedureActions.clearObservationUnableToCapture;

export const markDiagnosticObservationUnableToCapture = diagnosticActions.markObservationUnableToCapture;
export const clearDiagnosticObservationUnableToCapture = diagnosticActions.clearObservationUnableToCapture;

export const moveStep = orderSetAction(
  moveStepInputSchema,
  (input: MoveStepInput) => {
    const { props, prior } = graphFor(input.orderSetId);
    const set = OrderSet.restore(props);
    set.moveStep(input.stepIndex, input.offsetMinutes, schedulingFacts(prior.anchor.domainId));
    writeGraph(set.snapshot(), prior);
  },
  (input: MoveStepInput, options: WriteOptions) => getTRPCClient().orderSet.moveStep.mutate(input, options),
);

export const resizeStep = orderSetAction(
  resizeStepInputSchema,
  (input: ResizeStepInput) => {
    const { props, prior } = graphFor(input.orderSetId);
    const set = OrderSet.restore(props);
    set.resizeStep(input.stepIndex, input.durationMinutes, schedulingFacts(prior.anchor.domainId));
    writeGraph(set.snapshot(), prior);
  },
  (input: ResizeStepInput, options: WriteOptions) => getTRPCClient().orderSet.resizeStep.mutate(input, options),
);

export const resetTiming = orderSetAction(
  resetTimingInputSchema,
  (input: ResetTimingInput) => {
    const { props, prior } = graphFor(input.orderSetId);
    const set = OrderSet.restore(props);
    if (!set.protocol) throw new Error("This order set has no protocol timing to reset to");
    const protocol = selectedProtocol(set.protocol.url, Number(set.protocol.version));
    const placements = protocol.facts.steps.flatMap((step) =>
      step.occurrences.flatMap((occurrence) =>
        set.orders
          .filter((order) => order.sourceOccurrenceId === occurrence.id)
          .map((order) => ({
            orderId: order.orderId,
            placement: step.placement,
          })),
      ),
    );
    set.resetTiming(
      { placements, envelopeMinutes: protocol.facts.envelopeMinutes },
      schedulingFacts(prior.anchor.domainId),
    );
    writeGraph(set.snapshot(), prior);
  },
  (input: ResetTimingInput, options: WriteOptions) => getTRPCClient().orderSet.resetTiming.mutate(input, options),
);

export const applyOrderSetChanges = orderSetAction(
  applyOrderSetChangesInputSchema,
  (input: ApplyOrderSetChangesInput) => {
    const { props, prior } = graphFor(input.orderSetId);
    const set = replayOrderSetChanges(props, input.changes, schedulingFacts(prior.anchor.domainId));
    writeGraph(set.snapshot(), prior);
  },
  (input: ApplyOrderSetChangesInput, options: WriteOptions) =>
    getTRPCClient().orderSet.applyChanges.mutate(input, options),
);

export const saveOrderSetDraft = orderSetAction(
  orderSetDraftInputSchema,
  (input: OrderSetDraftInput) => {
    const { props, prior } = graphFor(input.orderSetId);
    const set = OrderSet.restore(props);
    set.saveDraft();
    writeGraph(set.snapshot(), prior);
  },
  (input: OrderSetDraftInput, options: WriteOptions) => getTRPCClient().orderSet.saveDraft.mutate(input, options),
);

export const discardOrderSetDraft = orderSetAction(
  orderSetDraftInputSchema,
  (input: OrderSetDraftInput) => {
    const { props, prior } = graphFor(input.orderSetId);
    const set = OrderSet.restore(props);
    set.discardDraft();
    writeGraph(set.snapshot(), prior);
  },
  (input: OrderSetDraftInput, options: WriteOptions) => getTRPCClient().orderSet.discardDraft.mutate(input, options),
);

export const importDiagnosticDocument = diagnosticActions.importDocument;

const procedureTrialDependencies: ProcedureTrialDependencies = {
  procedures: procedureRows,
  observations: observationRows,
  recordTrials: (input, options) => getTRPCClient().charting.procedure.recordTrials.mutate(input, options),
  correctTrial: (input, options) => getTRPCClient().charting.procedure.correctTrial.mutate(input, options),
  retractTrial: (input, options) => getTRPCClient().charting.procedure.retractTrial.mutate(input, options),
};

/** One trial API for both kinds. The kind only shapes the optimistic row; the server reads it off the order. */
export const procedureTrialActions: Record<"treatment" | "diagnostic", ProcedureTrialActions> = {
  treatment: createProcedureTrialActions(procedureTrialDependencies, treatmentProcedureActions.trialKind),
  diagnostic: createProcedureTrialActions(procedureTrialDependencies, diagnosticActions.trialKind),
};
